/**
 * `string-index` (`LN-WGT03`, the shape proposed in the `LN-JS-M3` draft): a
 * JavaScript string as the row of UTF-16 code units the engine stores and
 * counts, the characters a reader sees drawn under it, and a list of
 * expressions that each point at boxes by index.
 *
 * <div class="pc-widget" data-widget="string-index" data-name="s"
 *      data-units="[&quot;0041&quot;,&quot;006D&quot;]"
 *      data-steps="[{&quot;expr&quot;:&quot;s[0]&quot;,&quot;from&quot;:0,&quot;to&quot;:1,&quot;result&quot;:&quot;\&quot;A\&quot;&quot;,&quot;note&quot;:&quot;Index 0 is the first box.&quot;}]"
 *      data-caption="Each call points at boxes by index.">
 *   <p class="pc-widget-fallback">"Am" is two boxes; s[0] is "A".</p>
 * </div>
 *
 * `data-name` (optional, default `s`). `data-units` (required): the code
 * units as four-digit hex strings; each box draws its character, and a lone
 * surrogate half (or a control character) its hex. `data-graphemes`
 * (optional): a JSON list of `[from, to)` code-unit ranges, one per character
 * a reader sees, drawn as a second row under the boxes. `data-steps`
 * (required): a JSON list of `{ expr, from?, to?, marks?, result, note }`:
 * `from` and `to` the highlighted range (equal for an empty range, drawn as a
 * caret; at the length, a past-the-end slot), `marks` indexes with a pointer
 * under them, `result` the value as source text, `note` one sentence in the
 * lesson's language. `data-mark-labels` (optional, the `LN-JS-M3` review): a
 * JSON list of labels, one per pointer of a step's `marks` by position
 * (`["i","j"]`); without it the pointers are unlabelled. `data-caption`
 * (optional). The first frame is the boxes
 * with the first step's highlight. Fields a later review adds are optional.
 */

import {
  attachStepper,
  buildShell,
  el,
  isIndex,
  isRecord,
  liveNote,
  parseJson,
  type WidgetText,
} from "./dom";
import { visibleGlyph } from "./string-bytes";

interface StringStep {
  expr: string;
  from?: number;
  to?: number;
  marks?: number[];
  result: string;
  note: string;
}

const isUnits = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length > 0 && value.every((u) => typeof u === "string" && /^[0-9a-fA-F]{4}$/.test(u));

const isGraphemes =
  (length: number) =>
  (value: unknown): value is [number, number][] =>
    Array.isArray(value) &&
    value.every(
      (range) =>
        Array.isArray(range) &&
        range.length === 2 &&
        isIndex(range[0]) &&
        isIndex(range[1]) &&
        range[0] < range[1] &&
        range[1] <= length,
    );

const isSteps =
  (length: number) =>
  (value: unknown): value is StringStep[] =>
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (step) =>
        isRecord(step) &&
        typeof step.expr === "string" &&
        typeof step.result === "string" &&
        typeof step.note === "string" &&
        // A range is both ends or neither, start before end, inside the string.
        ((step.from === undefined && step.to === undefined) ||
          (isIndex(step.from) && isIndex(step.to) && step.from <= step.to && step.to <= length)) &&
        (step.marks === undefined ||
          (Array.isArray(step.marks) && step.marks.every((m) => isIndex(m) && m <= length))),
    );

const isLabels = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((label) => typeof label === "string");

/** What a box shows: its character, or its hex when the character draws as nothing. */
function glyph(unit: number): string {
  const hex = unit.toString(16).toUpperCase().padStart(4, "0");
  if (unit >= 0xd800 && unit <= 0xdfff) return `0x${hex}`;
  if (unit === 0x20) return "␣";
  if (unit < 0x20 || (unit >= 0x7f && unit <= 0x9f)) return `0x${hex}`;
  return visibleGlyph(String.fromCharCode(unit));
}

export function renderStringIndex(host: HTMLElement, text: WidgetText): boolean {
  const units = parseJson(host.dataset.units, isUnits);
  if (!units) return false;
  const length = units.length;
  const steps = parseJson(host.dataset.steps, isSteps(length));
  if (!steps) return false;
  let graphemes: [number, number][] | null = null;
  if (host.dataset.graphemes !== undefined) {
    graphemes = parseJson(host.dataset.graphemes, isGraphemes(length));
    if (!graphemes) return false;
  }
  // `data-mark-labels` (optional, the `LN-JS-M3` review): the label under
  // each pointer of `marks`, by position (`["i","j"]`).
  let markLabels: string[] | null = null;
  if (host.dataset.markLabels !== undefined) {
    markLabels = parseJson(host.dataset.markLabels, isLabels);
    if (!markLabels) return false;
  }
  const codes = units.map((u) => parseInt(u, 16));

  const shell = buildShell(host, {
    label: text("labelString"),
    caption: host.dataset.caption,
  });

  // The past-the-end slot is drawn only when a step points at it.
  const pastEnd = steps.some((s) => s.from === length || (s.marks ?? []).includes(length));
  const columns = length + (pastEnd ? 1 : 0);

  const grid = el("div", "pc-str-grid");
  grid.style.gridTemplateColumns = `repeat(${columns}, auto)`;

  const cells = codes.map((code, i) => {
    const cell = el("div", "pc-cell pc-str-unit");
    cell.style.gridColumn = String(i + 1);
    cell.style.gridRow = "1";
    cell.append(el("span", "pc-cell-value", glyph(code)));
    cell.append(el("span", "pc-cell-meta", units[i].toUpperCase()));
    grid.append(cell);
    return cell;
  });
  if (pastEnd) {
    const slot = el("div", "pc-cell is-spare pc-str-unit");
    slot.style.gridColumn = String(length + 1);
    slot.style.gridRow = "1";
    slot.append(el("span", "pc-cell-value", ""));
    slot.append(el("span", "pc-cell-meta", ""));
    grid.append(slot);
    cells.push(slot);
  }

  const marks = cells.map((_, i) => {
    const index = el("span", "pc-str-index", String(i));
    index.style.gridColumn = String(i + 1);
    index.style.gridRow = "2";
    grid.append(index);
    const mark = el("span", "pc-str-mark");
    mark.style.gridColumn = String(i + 1);
    mark.style.gridRow = "3";
    grid.append(mark);
    return mark;
  });

  if (graphemes) {
    for (const [from, to] of graphemes) {
      const seen = el("div", "pc-str-grapheme", String.fromCharCode(...codes.slice(from, to)));
      seen.style.gridColumn = `${from + 1} / ${to + 1}`;
      seen.style.gridRow = "4";
      grid.append(seen);
    }
  }

  const legend = el("p", "pc-str-legend");
  legend.append(el("span", undefined, text("stringUnits", { n: length })));
  if (graphemes) legend.append(el("span", undefined, text("stringGraphemes", { n: graphemes.length })));

  const expression = el("p", "pc-str-expr");
  const note = liveNote();
  shell.body.append(grid, legend, expression, note);

  attachStepper(
    shell,
    steps.length,
    (index) => {
      const step = steps[index];
      const empty = step.from !== undefined && step.from === step.to;
      cells.forEach((cell, i) => {
        const inside = step.from !== undefined && step.to !== undefined && i >= step.from && i < step.to;
        cell.classList.toggle("is-active", inside);
        cell.classList.toggle("is-caret", empty && i === step.from);
      });
      const pointers = step.marks ?? [];
      marks.forEach((mark, i) => {
        const here = pointers.flatMap((at, k) => (at === i ? [markLabels?.[k] ?? ""] : []));
        const names = here.filter(Boolean).join(" ");
        mark.textContent = here.length === 0 ? "" : names ? `↑ ${names}` : "↑";
      });
      expression.textContent = "";
      expression.append(el("code", undefined, step.expr), el("span", "pc-str-arrow", "→"), el("code", undefined, step.result));
      note.textContent = step.note;
    },
    text,
    { liveReadout: false },
  );
  return true;
}
